import React, { useEffect, useRef, useState, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions';

// wavesurfer.js's destroy() aborts an in-flight audio decode fetch. If that
// abort races a component unmounting (e.g. closing the trimmer before the
// waveform finishes loading), the rejection isn't caught internally and
// surfaces as an unhandled promise rejection — a known wavesurfer.js v7
// issue, harmless but noisy in React's dev error overlay. The rejection can
// land a macrotask or two after destroy() returns, so this filter is
// installed once, globally, rather than scoped per-unmount (a scoped
// listener risks being removed before the delayed rejection fires).
if (typeof window !== 'undefined' && !window.__wavesurferAbortFilterInstalled) {
  window.__wavesurferAbortFilterInstalled = true;
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason?.name === 'AbortError' && /signal is aborted/i.test(event.reason?.message || '')) {
      event.preventDefault();
    }
  });
}

function formatTime(seconds) {
  if (!isFinite(seconds)) return '0:00.0';
  const m = Math.floor(seconds / 60);
  const s = (seconds % 60).toFixed(1).padStart(4, '0');
  return `${m}:${s}`;
}

export default function WaveformTrimmer({ audioUrl, onTrim, onCancel, isSaving }) {
  const containerRef = useRef(null);
  const wsRef = useRef(null);
  const regionsRef = useRef(null);
  const regionRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [duration, setDuration] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!containerRef.current || !audioUrl) return;

    const regions = RegionsPlugin.create();
    regionsRef.current = regions;

    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: '#93c5fd',
      progressColor: '#2563eb',
      cursorColor: '#1d4ed8',
      height: 80,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      normalize: true,
      plugins: [regions],
    });
    wsRef.current = ws;

    // Destroying wavesurfer while its audio fetch is still in flight aborts
    // that fetch, and the rejection isn't reachable from outside (it's an
    // internal promise wavesurfer owns) — trying to catch/suppress it after
    // the fact is unreliable. Instead, never destroy mid-fetch: if the
    // component unmounts before 'ready'/'error' fires, just remember that
    // and destroy once the load naturally settles, when nothing is left to
    // abort.
    let unmounted = false;
    let settled = false;

    ws.on('ready', () => {
      settled = true;
      if (unmounted) { ws.destroy(); return; }

      const dur = ws.getDuration();
      setDuration(dur);
      setTrimStart(0);
      setTrimEnd(dur);
      setLoading(false);

      const region = regions.addRegion({
        start: 0,
        end: dur,
        color: 'rgba(37, 99, 235, 0.15)',
        drag: true,
        resize: true,
        minLength: 0.1,
      });
      regionRef.current = region;
    });

    ws.on('error', (err) => {
      settled = true;
      if (unmounted) { ws.destroy(); return; }
      console.error('WaveSurfer error:', err);
      setError('Could not load audio. Try refreshing.');
      setLoading(false);
    });

    ws.on('play', () => setPlaying(true));
    ws.on('pause', () => setPlaying(false));
    ws.on('finish', () => setPlaying(false));

    regions.on('region-updated', (region) => {
      setTrimStart(region.start);
      setTrimEnd(region.end);
    });

    ws.load(audioUrl);

    return () => {
      unmounted = true;
      wsRef.current = null;
      regionRef.current = null;
      if (settled) ws.destroy();
    };
  }, [audioUrl]);

  const togglePlay = useCallback(() => {
    if (!wsRef.current) return;
    if (playing) {
      wsRef.current.pause();
    } else {
      // Play only the selected region
      const r = regionRef.current;
      if (r) {
        wsRef.current.play(r.start, r.end);
      } else {
        wsRef.current.play();
      }
    }
  }, [playing]);

  const trimDuration = trimEnd - trimStart;
  const startPct = duration > 0 ? (trimStart / duration * 100).toFixed(1) : 0;
  const endPct = duration > 0 ? (trimEnd / duration * 100).toFixed(1) : 100;
  const removedStart = trimStart;
  const removedEnd = duration - trimEnd;

  return (
    <div className="mt-3 bg-white border border-blue-200 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243zm0-5.758L6.343 6.343" />
          </svg>
          <span className="text-sm font-semibold text-blue-800">Edit Audio</span>
        </div>
        <span className="text-xs text-blue-500">Drag the blue handles to set trim points</span>
      </div>

      {/* Waveform */}
      <div className="px-4 pt-4 pb-2">
        {loading && (
          <div className="flex items-center justify-center h-20 gap-2 text-sm text-gray-400">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400" />
            Loading waveform…
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center h-20 text-sm text-red-500">{error}</div>
        )}
        <div ref={containerRef} className={loading || error ? 'hidden' : ''} />
      </div>

      {/* Time readout */}
      {!loading && !error && (
        <div className="px-4 pb-3">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>0:00.0</span>
            <span>{formatTime(duration)}</span>
          </div>
          <div className="flex items-center gap-3 text-xs mt-2">
            <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2 space-y-0.5">
              <div className="text-gray-400 uppercase tracking-wide text-[10px]">Trim start</div>
              <div className="font-mono font-semibold text-blue-700">{formatTime(trimStart)}</div>
              {removedStart > 0.05 && (
                <div className="text-orange-500 text-[10px]">removes {formatTime(removedStart)} from front</div>
              )}
            </div>
            <div className="flex-1 bg-blue-50 rounded-lg px-3 py-2 space-y-0.5 border border-blue-100">
              <div className="text-blue-400 uppercase tracking-wide text-[10px]">Kept</div>
              <div className="font-mono font-semibold text-blue-800">{formatTime(trimDuration)}</div>
              <div className="text-blue-400 text-[10px]">{startPct}% – {endPct}%</div>
            </div>
            <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2 space-y-0.5">
              <div className="text-gray-400 uppercase tracking-wide text-[10px]">Trim end</div>
              <div className="font-mono font-semibold text-blue-700">{formatTime(trimEnd)}</div>
              {removedEnd > 0.05 && (
                <div className="text-orange-500 text-[10px]">removes {formatTime(removedEnd)} from back</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      {!loading && !error && (
        <div className="px-4 py-3 border-t border-blue-100 flex items-center gap-2">
          <button
            type="button"
            onClick={togglePlay}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors"
          >
            {playing ? (
              <>
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="4" width="4" height="16" rx="1"/>
                  <rect x="14" y="4" width="4" height="16" rx="1"/>
                </svg>
                Pause
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                Preview
              </>
            )}
          </button>

          <div className="flex-1" />

          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="px-3 py-2 rounded-lg text-sm text-gray-500 hover:text-gray-700 disabled:opacity-40 transition-colors"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={() => onTrim(trimStart, trimEnd)}
            disabled={isSaving || trimDuration < 0.1}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-40 transition-colors"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                Saving…
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243zm0-5.758L6.343 6.343" />
                </svg>
                Apply Trim
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
